import { toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { createApplicationApi } from '@/modules/service-requests/shared/application-api';
import { runServiceDeskWorkflow } from '@/modules/service-requests/shared/application-workflows';
import { asksMaternityBenefit } from '../utils/metlife-rules';
import { MetlifeApplyValues } from '../schemas/metlife-apply-form-schema';
import { MetlifeApplication, MetlifeStatus } from '../types/metlife.types';
import { METLIFE_FIELDS } from '../graphql/metlife.fields';

export const metlifeApi = createApplicationApi<MetlifeApplication>(
  'MetLifeApplication',
  METLIFE_FIELDS
);

interface SubmitMetlifeParams {
  existing?: MetlifeApplication;
  values: MetlifeApplyValues;
}

/**
 * Legacy `CreateMetLifeApplicationCommand`, now the METLIFE_APPLICATION_SUBMIT workflow: at most one
 * live application per type, no interns or employees on probation, the maternity answer only where it
 * is asked. A reviewer holding UPDATE_STATUS may edit someone else's application through it too.
 */
export const submitMetlifeApplication = ({ existing, values }: SubmitMetlifeParams) =>
  runServiceDeskWorkflow('METLIFE_APPLICATION_SUBMIT', {
    itemId: existing?.ItemId,
    typeOfApplication: values.typeOfApplication,
    metlifeIdNumber: values.metlifeIdNumber.trim(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim(),
    policyApplicantName: values.policyApplicantName.trim(),
    designation: values.designation.trim(),
    // Date only: the picked day at 00:00:00 UTC (hrm-timezone-standard).
    dateOfBirth: toDateOnlyValue(values.dateOfBirth),
    mobileNumber: values.mobileNumber.trim(),
    accountNumber: values.accountNumber.trim(),
    branchName: values.branchName.trim(),
    routingNumber: values.routingNumber.trim(),
    departmentId: values.departmentId,
    gender: values.gender,
    wantsMaternityBenefit:
      asksMaternityBenefit(values.typeOfApplication, values.gender) &&
      values.wantsMaternityBenefit !== ''
        ? values.wantsMaternityBenefit === 'yes'
        : null,
  });

interface ChangeMetlifeStatusParams {
  itemId: string;
  status: MetlifeStatus;
  /** Required when completing: the policy number issued by the insurer. */
  metLifeIdNumber?: string;
}

/**
 * Legacy `UpdateMetLifeApplicationStatusCommand` (METLIFE_APPLICATION_STATUS workflow). Excluding needs
 * its own permission and is only allowed from Completed; cancelling is the applicant's own path.
 */
export const changeMetlifeStatus = ({
  itemId,
  status,
  metLifeIdNumber,
}: ChangeMetlifeStatusParams) =>
  runServiceDeskWorkflow('METLIFE_APPLICATION_STATUS', {
    itemId,
    status,
    ...(metLifeIdNumber && { metLifeIdNumber: metLifeIdNumber.trim() }),
  });

interface CancelMetlifeParams {
  itemId: string;
  reason?: string;
}

/** Legacy `CancelMetLifeApplicationAsync`: the owner cancels a completed spouse policy. */
export const cancelMetlifeApplication = ({ itemId, reason }: CancelMetlifeParams) =>
  runServiceDeskWorkflow('METLIFE_APPLICATION_CANCEL', { itemId, reason: reason ?? '' });
